import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Cookie from "cookie-universal";
import "./NavBar.css";

function PersonIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
    </svg>
  );
}

function ProfileAvatar({ userName, onLogout, onDashboard }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="nav-profile-wrap" ref={ref}>
      <button
        className="nav-avatar-btn"
        onClick={() => setOpen((p) => !p)}
        title={userName || "الملف الشخصي"}
      >
        <PersonIcon size={20} />
      </button>

      {open && (
        <div className="nav-profile-dropdown">
          <div className="dropdown-user-info">
            <div className="dropdown-avatar-sm">
              <PersonIcon size={20} color="#fff" />
            </div>
            <div>
              <div className="dropdown-name">{userName || "مستخدم"}</div>
              <div className="dropdown-role">مسجّل الدخول</div>
            </div>
          </div>
          <button
            className="dropdown-btn dropdown-btn-dashboard"
            onClick={() => { setOpen(false); onDashboard(); }}
          >
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
            </svg>
            لوحة التحكم
          </button>
          <button
            className="dropdown-btn"
            onClick={() => { setOpen(false); onLogout(); }}
          >
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
            </svg>
            تسجيل الخروج
          </button>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("");

  const cookie = Cookie();
  const isLoggedIn = !!cookie.get("rasheed");
  const userName = cookie.get("user_name") || "";

  const navigate = useNavigate();
  const location = useLocation();

  const guestLinks = [
    { name: "الرئيسية",         path: "/"                   },
    { name: "المواد",           path: "#materials"           },
    { name: "الدورات المكثفة", path: "#courses"             },
    { name: "الطلاب المميزين", path: "#excellent-students"  },
  ];

  // When logged in, only Home is shown as a regular link; Dashboard is a separate button
  const adminLinks = [
    { name: "الرئيسية", path: "/" },
  ];

  const navLinks = isLoggedIn ? adminLinks : guestLinks;

  useEffect(() => {
    const current = navLinks.find(
      (link) => location.pathname === link.path || location.hash === link.path
    );
    if (current) setActive(current.name);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* close sidebar on route change */
  useEffect(() => { setOpen(false); }, [location]);

  const handleAnchorScroll = (anchor) => {
    const el = document.getElementById(anchor);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState(null, "", `#${anchor}`);
    }
  };

  const handleLinkClick = (name, path) => {
    setActive(name);
    setOpen(false);
    if (path === "/") {
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 150);
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }
    if (path.startsWith("#")) {
      const anchor = path.slice(1);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => handleAnchorScroll(anchor), 150);
      } else {
        handleAnchorScroll(anchor);
      }
      return;
    }
    navigate(path);
  };

  const handleLogout = () => {
    const opts = { path: "/" };
    cookie.remove("rasheed", opts);
    cookie.remove("user_name", opts);
    cookie.remove("refresh_token", opts);
    navigate("/login");
  };

  return (
    <>
      <nav className={`navbar-pro${scrolled ? " scrolled" : ""}`}>
        <div className="navbar-container">
          <h1 className="logo-text" onClick={() => navigate("/")}>
            أكادمية التعليم
          </h1>

          {/* Desktop links */}
          {!isMobile && (
            <ul className="nav-links">
              {navLinks.map((link) => (
                <li
                  key={link.name}
                  className={`nav-link${active === link.name ? " active" : ""}`}
                  onClick={() => handleLinkClick(link.name, link.path)}
                >
                  {link.name}
                </li>
              ))}
              {isLoggedIn ? (
                <>
                  <li>
                    <button
                      className="dashboard-btn"
                      onClick={() => navigate("/dashboard")}
                    >
                      <svg width="15" height="15" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
                      </svg>
                      لوحة التحكم
                    </button>
                  </li>
                  <li>
                    <ProfileAvatar
                      userName={userName}
                      onLogout={handleLogout}
                      onDashboard={() => navigate("/dashboard")}
                    />
                  </li>
                </>
              ) : (
                <li>
                  <button className="login-btn" onClick={() => navigate("/login")}>
                    تسجيل الدخول
                  </button>
                </li>
              )}
            </ul>
          )}

          {/* Mobile controls */}
          {isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {isLoggedIn ? (
                <ProfileAvatar
                  userName={userName}
                  onLogout={handleLogout}
                  onDashboard={() => { setOpen(false); navigate("/dashboard"); }}
                />
              ) : (
                <button
                  className="login-btn"
                  style={{ padding: "7px 18px", fontSize: "0.85rem" }}
                  onClick={() => navigate("/login")}
                >
                  تسجيل الدخول
                </button>
              )}
              <div
                className={`menu-icon${open ? " open" : ""}`}
                onClick={() => setOpen((p) => !p)}
                aria-label="Toggle menu"
              >
                <span />
                <span />
                <span />
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Overlay */}
      {isMobile && (
        <div
          className={`overlay${open ? " show" : ""}`}
          onClick={() => setOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      {isMobile && (
        <div className={`sidebar${open ? " open" : ""}`}>
          <div className="sidebar-header">
            <span className="sidebar-logo">أولي النهى</span>
            <button className="sidebar-close" onClick={() => setOpen(false)}>✕</button>
          </div>

          <ul className="sidebar-ul">
            {navLinks.map((link) => (
              <li
                key={link.name}
                className={`sidebar-link${active === link.name ? " active" : ""}`}
                onClick={() => handleLinkClick(link.name, link.path)}
              >
                {link.name}
              </li>
            ))}
            {isLoggedIn && (
              <li
                className="sidebar-link sidebar-link-dashboard"
                onClick={() => { setOpen(false); navigate("/dashboard"); }}
              >
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24" style={{ marginLeft: 8 }}>
                  <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
                </svg>
                لوحة التحكم
              </li>
            )}
          </ul>

          {!isLoggedIn && (
            <div className="sidebar-footer">
              <button className="sidebar-login-btn" onClick={() => { setOpen(false); navigate("/login"); }}>
                تسجيل الدخول
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
